import { type FC, useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { Link } from 'react-router-dom'

import placeholderAvatar from 'assets/images/user_placeholder.png'
import {
  IconChatOutline,
  IconHeartFill,
  IconHeartOutline,
  IconLiveCircleTip,
  IconPostStats,
  IconPostStatsActive,
} from 'assets/svg/sprite'
import './_previewActions.scss'
import TipModal from 'components/UI/Modal/Tip/TipModal'
import StatsPopup from 'components/UI/StatsPopup/StatsPopup'
import { ViewLikesModal } from 'components-ref/organisms'
import { useModalContext } from 'context/modalContext'
import { usePreviewContext } from 'context/previewContext'
import { useLikePost } from 'helpers/hooks'
import { useUserProfile } from 'store'
import { usePreviewStore } from 'store/previewStore'
import { type IPost, type IStatsData } from 'types/interfaces/ITypes'

interface Props {
  query: any[]
  postId: string
  isLiked: boolean
  likes: number
  comments: number
  stats: IStatsData
  setCommentsActive: (value: boolean) => void
  entityType?: string
  modelData: {
    postId: string
    modelId: string
    avatarSrc: string
    modelUsername?: string
  }
  newPostPage?: boolean
}

interface InfiniteData<TData> {
  pages: TData[]
  pageParams: unknown[]
}

interface IInfinitePage {
  nextCursor: number | undefined
  page: {
    data: {
      data: IPost[]
    }
  }
}

const PreviewActions: FC<Props> = ({
  query,
  postId,
  isLiked,
  likes,
  comments,
  stats,
  setCommentsActive,
  entityType,
  modelData,
  newPostPage,
}) => {
  const [showLikesModal, setShowLikesModal] = useState(false)
  const [activeStats, setActiveStats] = useState(false)

  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const modalData = useModalContext()
  const { clearModal } = usePreviewContext()
  const user = useUserProfile()
const commentCountOverride = usePreviewStore(
  s => s.commentCountByPostId[String(postId)]
)

const shownComments = commentCountOverride ?? comments
  const isOwnPost = user?.id === modelData.modelId
  const isMessageEntity = entityType === 'message'
  const canShowEngagementActions = !isMessageEntity
  const canShowStats = !newPostPage && isOwnPost && !isMessageEntity

  const cachedData = queryClient.getQueryData<InfiniteData<IInfinitePage>>(query)

  useEffect(() => {
    if (!modalData.isOpen) {
      setActiveStats(false)
    }
  }, [modalData.isOpen])

const onLikeMutate = () => {
  if (!cachedData || isMessageEntity) return

  const nextLiked = !isLiked
  const nextLikes = nextLiked
    ? likes + 1
    : Math.max(likes - 1, 0)

  const newData = {
    ...cachedData,
    pages: cachedData.pages.map(singleFeedPage => ({
      ...singleFeedPage,
      page: {
        ...singleFeedPage.page,
        data: {
          ...singleFeedPage.page.data,
          data: singleFeedPage.page.data.data.map(post =>
            post.id === postId
              ? {
                  ...post,
                  isLiked: nextLiked,
                  likes_count: nextLikes,
                }
              : post
          ),
        },
      },
    })),
  }

  queryClient.setQueryData(query, newData)

  const currentSelectedPost =
    usePreviewStore.getState().selectedPostData

  if (currentSelectedPost?.id === postId) {
    usePreviewStore.getState().setSelectedPostData({
      ...currentSelectedPost,
      isLiked: nextLiked,
      likes_count: nextLikes,
    })
  }
}

  const onMutateError = () => {
    queryClient.setQueryData(query, cachedData)
  }

  const { likePost } = useLikePost(
    postId,
    isLiked,
    onLikeMutate,
    onMutateError
  )

  const handleLikeClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.preventDefault()
    e.stopPropagation()

    if (isMessageEntity) return

    if (isOwnPost) {
      setShowLikesModal(true)
    } else {
      likePost.mutate()
      // engagePost()
    }
  }

  const handleTip = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation()

    modalData.addModal(
      t('tipAmount'),
      <TipModal tipType='post' modelData={modelData} />
    )

    // engagePost()
  }

  return (
    <>
      {showLikesModal && canShowEngagementActions && (
        <ViewLikesModal
          onClose={() => setShowLikesModal(false)}
          postId={postId}
        />
      )}

      <div className='previewActions'>
        <Link
          to={`/${modelData.modelUsername}`}
          className='previewActions__avatar'
          onClick={e => {
            e.stopPropagation()
            clearModal()
          }}
        >
          <div className='previewActions__avatar--ring'>
            <img
              src={modelData.avatarSrc || placeholderAvatar}
              alt='avatar'
            />
          </div>
        </Link>

        {canShowStats && (
          <div
            className='previewAction'
            onClick={e => {
              e.stopPropagation()
              setActiveStats(true)
              modalData.addModal(
                '',
                <StatsPopup
                  statsData={stats}
                  closeStats={() => modalData.clearModal()}
                />,
                true,
                true,
                'previewStats'
              )
            }}
          >
            {activeStats ? <IconPostStatsActive /> : <IconPostStats />}
            <span>${stats.overallRevenue}</span>
          </div>
        )}

        {canShowEngagementActions && (
          <div className='previewAction' onClick={handleLikeClick}>
            {isLiked ? <IconHeartFill /> : <IconHeartOutline color='#fff' />}
            <span>{likes}</span>
          </div>
        )}

        {canShowEngagementActions && (
          <div
            className='previewAction'
            onClick={e => {
              e.stopPropagation()
              setCommentsActive(true)
            }}
          >
            <IconChatOutline />
            <span>{shownComments}</span>
          </div>
        )}

        {!isOwnPost && (
          <div className='previewAction' onClick={handleTip}>
            <IconLiveCircleTip />
          </div>
        )}

        {newPostPage && (
          <div className='previewAction' onClick={handleTip}>
            <IconLiveCircleTip />
          </div>
        )}
      </div>
    </>
  )
}

export default PreviewActions